import 'prop-types';
import { useState } from 'react';
import _get from 'lodash/get';
import { useDevice } from '@atc/react-device';
import { SideBarToModal, Heading, Accordion, AccordionPanel, Tab, Nav, NavItem, Glyphicon, Alert, Text } from 'reaxl';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
const formatDate = date => {
  const dateObj = new Date(date);
  return dateObj.toString() !== 'Invalid Date' ? dateObj.toLocaleDateString() : date;
};

/**
 *  The AvailableOffers component renders a SideBar or a Modal with offers information on it
 */
function AvailableOffers(_ref) {
  let {
    offers = [],
    onHide = () => {},
    onOfferTabClick = () => {},
    show = false,
    wrappedInModal,
    ...rest
  } = _ref;
  const device = useDevice();
  const isXs = _get(device, 'is.xs', true);
  const [showAlert, setShowAlert] = useState(true);
  if (!offers.length) {
    return null;
  }

  // group offers into individual categories
  const categorizedOffers = offers.reduce((acc, offer) => {
    const {
      category
    } = offer;
    if (category) {
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(offer);
    }
    return acc;
  }, {});
  const renderDetails = details => {
    if (typeof details === 'string') {
      return /*#__PURE__*/jsx("p", {
        children: details
      });
    }
    return /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsx("div", {
        className: "text-bold margin-vertical-3",
        children: "Offer Details"
      }), details.map((detail, index) => /*#__PURE__*/jsx("div", {
        children: detail
      }, `${detail}-${index}`))]
    });
  };
  const renderOemDisclaimers = oemDisclaimers => oemDisclaimers.map((disclaimer, index) => /*#__PURE__*/jsxs("div", {
    children: [/*#__PURE__*/jsx("div", {
      children: disclaimer.programName
    }), /*#__PURE__*/jsx("div", {
      children: disclaimer.disclaimer
    })]
  }, `${disclaimer.programName}-${index}`));
  const renderOfferContent = (expirationDate, details, additionalOffers, legalDisclaimer, oemDisclaimers) => /*#__PURE__*/jsxs(Fragment, {
    children: [!!expirationDate && /*#__PURE__*/jsxs("div", {
      className: "text-bold",
      children: ["Expires ", formatDate(expirationDate)]
    }), !!details && !!details.length && renderDetails(details), !!additionalOffers && /*#__PURE__*/jsxs("div", {
      children: [/*#__PURE__*/jsx("div", {
        className: "text-bold margin-vertical-3",
        children: "Additional offers you may qualify for"
      }), /*#__PURE__*/jsx("div", {
        children: additionalOffers
      })]
    }), (!!legalDisclaimer || !!oemDisclaimers) && /*#__PURE__*/jsxs("div", {
      children: [/*#__PURE__*/jsx("div", {
        className: "text-bold margin-vertical-3",
        children: "Disclaimer"
      }), !!legalDisclaimer && /*#__PURE__*/jsx("div", {
        dangerouslySetInnerHTML: {
          __html: legalDisclaimer
        }
      }), !!oemDisclaimers && renderOemDisclaimers(oemDisclaimers)]
    })]
  });
  const renderOfferTitle = (offerTitle, isAppliedOffer) => /*#__PURE__*/
  // required this span to override accordion title default styles
  jsxs("span", {
    children: [isAppliedOffer && /*#__PURE__*/jsx(Glyphicon, {
      glyph: "success",
      className: "text-success margin-right-1"
    }), offerTitle]
  });
  const renderOffersAccordion = (availableOffers, autoOpen) => !!availableOffers.length && /*#__PURE__*/jsx(Accordion, {
    bsStyle: "filters",
    onOpen: onOfferTabClick,
    defaultActiveKey: autoOpen ? 0 : undefined,
    children: availableOffers.map((_ref2, index) => {
      let {
        name,
        expirationDate,
        details,
        additionalOffers,
        legalDisclaimer,
        oemDisclaimers,
        isAppliedOffer
      } = _ref2;
      return /*#__PURE__*/jsx(AccordionPanel, {
        title: renderOfferTitle(name, isAppliedOffer),
        eventKey: index,
        children: renderOfferContent(expirationDate, details, additionalOffers, legalDisclaimer, oemDisclaimers)
      }, name + index);
    })
  });
  const renderOffersTabs = () => /*#__PURE__*/jsx(Tab.Container, {
    id: "offers",
    defaultActiveKey: 0,
    onSelect: onOfferTabClick,
    children: /*#__PURE__*/jsxs("div", {
      className: "row margin-horizontal-0",
      children: [/*#__PURE__*/jsx("div", {
        className: "col-xs-4",
        children: /*#__PURE__*/jsx(Nav, {
          bsStyle: "pills",
          stacked: true,
          children: offers.map((_ref3, index) => {
            let {
              name
            } = _ref3;
            return /*#__PURE__*/jsx(NavItem, {
              eventKey: index,
              children: name
            }, index);
          })
        })
      }), /*#__PURE__*/jsx("div", {
        className: "col-xs-8",
        children: /*#__PURE__*/jsx(Tab.Content, {
          animation: true,
          children: offers.map((_ref4, index) => {
            let {
              expirationDate,
              details,
              additionalOffers,
              legalDisclaimer,
              oemDisclaimers
            } = _ref4;
            return /*#__PURE__*/jsx(Tab.Pane, {
              eventKey: index,
              children: renderOfferContent(expirationDate, details, additionalOffers, legalDisclaimer, oemDisclaimers)
            }, index);
          })
        })
      })]
    })
  });
  const categorizedOffersKeys = Object.keys(categorizedOffers);
  const categorizedOffersLength = categorizedOffersKeys.length;
  const renderOffersWithCategories = () => categorizedOffersKeys.map((category, index) => {
    const isOneOffer = categorizedOffersLength === 1 && categorizedOffers[category].length === 1;
    return /*#__PURE__*/jsxs("div", {
      children: [index !== 0 && /*#__PURE__*/jsx("hr", {
        className: "margin-vertical-2"
      }), category && /*#__PURE__*/jsx(Heading, {
        componentClass: "div",
        size: 400,
        children: category
      }), renderOffersAccordion(categorizedOffers[category], isOneOffer)]
    }, category);
  });
  let sideBar;
  let modal;
  if (offers[0]?.hasError) {
    sideBar = showAlert ? /*#__PURE__*/jsxs(Alert, {
      bsStyle: "danger",
      onDismiss: () => setShowAlert(false),
      children: [/*#__PURE__*/jsx(Text, {
        weight: "bold",
        children: "Oops, we hit a bump in the road."
      }), " Offer information is unavailable at the moment. Please try again later."]
    }) : null;
    modal = sideBar;
  } else if (categorizedOffersLength) {
    sideBar = renderOffersWithCategories();
    modal = sideBar;
  } else {
    sideBar = renderOffersAccordion(offers, offers.length === 1);
    modal = renderOffersTabs();
  }
  if (wrappedInModal) {
    return sideBar;
  }
  return /*#__PURE__*/jsx(SideBarToModal, {
    "data-cmp": "availableOffers",
    title: "Available Offers",
    content: {
      sideBar,
      modal
    },
    collapsed: isXs,
    show: show,
    onHide: onHide,
    sideBarOptions: {
      fullWidth: true
    },
    modalOptions: {
      bsStyle: 'lg',
      scrollPanel: true
    },
    ...rest
  });
}
export { AvailableOffers as default };
